import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { StageDetails } from "../../types/tournament";
import {
  deleteKnockoutSchedule,
  previewKnockoutMatches,
  createKnockoutMatches,
  getGroupStandings,
  getLeagueStandings,
  getKnockoutMatches,
} from "../../api/tournaments";
import { IMatchDetailDTO } from "../../models/IMatch";
import { IGroupStanding, IGroupStandingList } from "../../models/IGroup";
import { ILeagueStanding } from "../../models/ILeague";
import { Button, Dialog, EmptyState, Field, FormGrid, Input, Spinner } from "../../ui";
import "./styles.scss";

type CompleteBracketDialogProps = {
  stage: StageDetails;
  isOpen: boolean;
  isLoadingStageDetails?: boolean;
  onClose: (update?: boolean) => void;
};

const CompleteBracketDialog = (props: CompleteBracketDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMatches, setPreviewMatches] = useState<IMatchDetailDTO[]>([]);
  const [rankedTeams, setRankedTeams] = useState<
    Array<{
      teamId: string;
      teamName: string;
      groupName?: string;
      position: number;
      points: number;
      setsDifference: number;
      pointDifference: number;
    }>
  >([]);
  const [showPreview, setShowPreview] = useState(false);
  const [fromPosition, setFromPosition] = useState<number | null>(null);
  const [toPosition, setToPosition] = useState<number | null>(null);
  const [fromPositionInput, setFromPositionInput] = useState<string>("");
  const [toPositionInput, setToPositionInput] = useState<string>("");
  const [allAvailableTeams, setAllAvailableTeams] = useState<
    Array<{
      teamId: string;
      teamName: string;
      groupName?: string;
      position: number;
      points: number;
      setsDifference: number;
      pointDifference: number;
    }>
  >([]);

  useEffect(() => {
    if (props.isOpen) {
      setShowPreview(false);
      setPreviewMatches([]);
      setRankedTeams([]);
      setAllAvailableTeams([]);

      // Autofill from knockout rules if available
      const knockoutRules = props.stage?.knockoutRules;
      const initialFromPos = knockoutRules?.positionFrom ?? null;
      const initialToPos = knockoutRules?.positionTo ?? null;

      setFromPosition(initialFromPos);
      setToPosition(initialToPos);
      setFromPositionInput(initialFromPos !== null ? String(initialFromPos) : "");
      setToPositionInput(initialToPos !== null ? String(initialToPos) : "");
    }
  }, [props.isOpen, props.stage?.knockoutRules]);

  const handleClose = (update?: boolean) => {
    props.onClose(update);
  };

  const fetchRankedTeams = async () => {
    if (!props.stage.precedingStages || props.stage.precedingStages.length === 0) {
      throw new Error("No preceding stages found");
    }

    // Find the group or league stage (not knockout) as the source for team rankings
    // For tier brackets (e.g., Tier B), we want teams from the group stage, not from Tier A
    const sourceStage =
      props.stage.precedingStages.find(
        (stage) => stage.stageName.includes("Group") || stage.stageName.includes("League")
      ) || props.stage.precedingStages[props.stage.precedingStages.length - 1];

    if (sourceStage.stageName.includes("Group")) {
      // Handle group stage
      const res = await getGroupStandings(sourceStage.id);
      const groupLists = res.data as IGroupStandingList[];

      // Organize teams by position across all groups
      const transposedByRank: IGroupStanding[][] = [];

      groupLists.forEach((group) => {
        const sorted = [...group.standings].sort((a, b) => {
          if (b.points !== a.points) return b.points - a.points;
          if (b.setsDifference !== a.setsDifference) return b.setsDifference - a.setsDifference;
          return b.pointDifference - a.pointDifference;
        });

        for (let i = 0; i < sorted.length; i++) {
          if (!transposedByRank[i]) transposedByRank[i] = [];
          if (sorted[i]) transposedByRank[i].push(sorted[i]);
        }
      });

      // Create ranked list: best 1st, second-best 1st, etc.
      const rankedTeams: Array<{
        teamId: string;
        teamName: string;
        groupName?: string;
        position: number;
        points: number;
        setsDifference: number;
        pointDifference: number;
      }> = [];

      let sequentialPosition = 1;
      transposedByRank.forEach((positionGroup) => {
        // Sort teams at same position by performance
        positionGroup.sort((a, b) => {
          if (b.points !== a.points) return b.points - a.points;
          if (b.setsDifference !== a.setsDifference) return b.setsDifference - a.setsDifference;
          return b.pointDifference - a.pointDifference;
        });

        // Assign sequential position to each team
        positionGroup.forEach((standing) => {
          rankedTeams.push({
            teamId: standing.teamId,
            teamName: standing.teamName,
            groupName: standing.groupName,
            position: sequentialPosition++, // Each team gets a unique sequential position
            points: standing.points,
            setsDifference: standing.setsDifference,
            pointDifference: standing.pointDifference,
          });
        });
      });

      return rankedTeams;
    } else {
      // Handle league stage
      const res = await getLeagueStandings(sourceStage.id);
      const standings = res.data as ILeagueStanding[];

      return standings.map((standing, index) => ({
        teamId: standing.teamId,
        teamName: standing.teamName,
        groupName: undefined, // League stages don't have groups
        position: index + 1,
        points: standing.points,
        setsDifference: standing.setsDifference,
        pointDifference: standing.pointDifference,
      }));
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const allTeams = await fetchRankedTeams();
      setAllAvailableTeams(allTeams);

      // Determine how many teams are needed based on the bracket structure
      // Get the current matches to see how many teams are expected
      const currentMatches = await getKnockoutMatches(props.stage.id);
      const firstRoundMatches = currentMatches.data.matches.filter((match) => {
        return !match.parentMatch1Id && !match.parentMatch2Id;
      });

      // Calculate how many teams are needed (2 teams per first round match)
      const teamsNeeded = firstRoundMatches.length * 2;

      // Find the group/league stage to get fromPosition and toPosition
      const sourceStage = props.stage.precedingStages.find(
        (stage) => stage.stageName.includes("Group") || stage.stageName.includes("League")
      );

      // Always prioritize knockout rules values if available
      const knockoutRules = props.stage?.knockoutRules;
      let initialFromPos = fromPosition;
      let initialToPos = toPosition;

      // Check knockout rules first - these should always take precedence
      if (
        knockoutRules?.positionFrom !== null &&
        knockoutRules?.positionFrom !== undefined &&
        knockoutRules?.positionTo !== null &&
        knockoutRules?.positionTo !== undefined
      ) {
        initialFromPos = knockoutRules.positionFrom;
        initialToPos = knockoutRules.positionTo;
      } else if (initialFromPos === null || initialToPos === null) {
        // Fallback to source stage or default values
        if (sourceStage && sourceStage.fromPosition && sourceStage.toPosition) {
          initialFromPos = sourceStage.fromPosition;
          initialToPos = sourceStage.toPosition;
        } else {
          initialFromPos = 1;
          initialToPos = teamsNeeded;
        }
      }

      // Always update state to ensure values match knockout rules
      setFromPosition(initialFromPos);
      setToPosition(initialToPos);
      setFromPositionInput(String(initialFromPos));
      setToPositionInput(String(initialToPos));

      // Filter teams based on position range
      const teams = allTeams.filter(
        (team) => team.position >= (initialFromPos || 1) && team.position <= (initialToPos || teamsNeeded)
      );

      setRankedTeams(teams);

      // Create team groups mapping for group stages
      const teamGroups: { [teamId: string]: string } = {};
      teams.forEach((team) => {
        if (team.groupName && team.groupName !== undefined) {
          teamGroups[team.teamId] = team.groupName;
        }
      });

      const payload = {
        teamIds: teams.map((t) => t.teamId),
        bestOf: 1,
        useSeeding: true,
        isRandom: true,
        ...(Object.keys(teamGroups).length > 0 && { teamGroups }),
      };

      const res = await previewKnockoutMatches(props.stage.id, payload);
      setPreviewMatches(res.data);
      setShowPreview(true);
    } catch (error: any) {
      console.error("Error previewing matches:", error.message);
      toast.error("Failed to preview matches. Please make sure the preceding stage is completed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePositionChange = async (from: number, to: number) => {
    setFromPosition(from);
    setToPosition(to);
    setFromPositionInput(String(from));
    setToPositionInput(String(to));

    // Filter teams based on new position range
    const filteredTeams = allAvailableTeams.filter((team) => team.position >= from && team.position <= to);
    setRankedTeams(filteredTeams);

    // Regenerate preview matches with the new team selection
    try {
      const teamGroups: { [teamId: string]: string } = {};
      filteredTeams.forEach((team) => {
        if (team.groupName && team.groupName !== undefined) {
          teamGroups[team.teamId] = team.groupName;
        }
      });

      const payload = {
        teamIds: filteredTeams.map((t) => t.teamId),
        bestOf: 1,
        useSeeding: true,
        isRandom: true,
        ...(Object.keys(teamGroups).length > 0 && { teamGroups }),
      };

      const res = await previewKnockoutMatches(props.stage.id, payload);
      setPreviewMatches(res.data);
    } catch (error: any) {
      console.error("Error updating preview matches:", error.message);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Step 1: Get current matches to preserve venue and time data
      const currentMatches = await getKnockoutMatches(props.stage.id);
      const existingMatches = currentMatches.data.matches;

      // Step 2: Delete existing schedule
      await deleteKnockoutSchedule(props.stage.id);

      // Step 3: Apply venue and time data from existing matches to preview matches
      // Since bracket is always null, we'll match by round and preserve venue assignments by round
      // Group existing matches by round to create a venue pool per round
      const venuesByRound: { [round: string]: Array<{ venue: string; startTime: string; startDate?: string }> } = {};

      existingMatches.forEach((match) => {
        if (!venuesByRound[match.round]) {
          venuesByRound[match.round] = [];
        }
        venuesByRound[match.round].push({
          venue: match.venue,
          startTime: match.startTime,
          startDate: match.startDate,
        });
      });

      // Apply venue/time data to preview matches by round, cycling through available venues
      const matchesWithVenueTime = previewMatches.map((match) => {
        const roundVenues = venuesByRound[match.round];

        if (!roundVenues || roundVenues.length === 0) {
          // No venue data for this round, keep preview defaults
          return match;
        }

        // Count how many matches we've already assigned in this round
        const matchesInRoundSoFar = previewMatches
          .slice(0, previewMatches.indexOf(match))
          .filter((m) => m.round === match.round).length;

        // Use modulo to cycle through venues if we have more matches than venue assignments
        const venueIndex = matchesInRoundSoFar % roundVenues.length;
        const venueData = roundVenues[venueIndex];

        return {
          ...match,
          venue: venueData.venue,
          startTime: venueData.startTime,
          startDate: venueData.startDate,
        };
      });

      // Step 4: Create the actual matches with preserved venue and time data
      await createKnockoutMatches(props.stage.id, matchesWithVenueTime);

      toast.success("Bracket completed successfully!");
      handleClose(true);
    } catch (error: any) {
      console.error("Error completing bracket:", error.message);
      toast.error("Failed to complete bracket. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      placement="side"
      size="sm"
      busy={loading}
      title="Complete Bracket"
      subtitle="This will delete the current schedule and create a new bracket using teams from the preceding stage based on their final rankings."
      className="complete-bracket"
      bodyClassName="complete-bracket__body"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()}>
            Cancel
          </Button>
          {showPreview ? (
            <Button variant="primary" loading={loading} onClick={handleComplete}>
              Complete
            </Button>
          ) : (
            <Button
              variant="primary"
              loading={previewLoading}
              disabled={props.isLoadingStageDetails}
              onClick={handlePreview}
            >
              Preview First
            </Button>
          )}
        </>
      }
    >
      {!showPreview ? (
        <div className="complete-bracket__intro">
          <p className="complete-bracket__text">Preview the bracket before completing:</p>
          {props.isLoadingStageDetails && (
            <p className="complete-bracket__loading">
              <Spinner size="sm" />
              Loading stage details...
            </p>
          )}
          <Button
            variant="tint"
            block
            loading={previewLoading}
            disabled={props.isLoadingStageDetails}
            onClick={handlePreview}
          >
            {previewLoading ? "Loading..." : "Preview Bracket"}
          </Button>
        </div>
      ) : (
        <div className="complete-bracket__results">
          <p className="complete-bracket__heading">Bracket Preview</p>

          <div className="complete-bracket__range">
            <p className="complete-bracket__label">Select Position Range:</p>
            <FormGrid columns={2} className="complete-bracket__inputs">
              <Field label="From Position:">
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    min="1"
                    max={allAvailableTeams.length}
                    value={fromPositionInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFromPositionInput(value); // Allow empty string

                      if (value === "") {
                        return; // Don't filter while empty
                      }

                      const from = parseInt(value);
                      if (isNaN(from) || from < 1) return;

                      const to = toPosition || allAvailableTeams.length;
                      if (from <= to && from <= allAvailableTeams.length) {
                        handlePositionChange(from, to);
                      }
                    }}
                    onBlur={(e) => {
                      // If empty on blur, set to 1
                      if (e.target.value === "") {
                        const to = toPosition || allAvailableTeams.length;
                        handlePositionChange(1, to);
                        setFromPositionInput("1");
                      }
                    }}
                  />
                )}
              </Field>
              <Field label="To Position:">
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    min="1"
                    max={allAvailableTeams.length}
                    value={toPositionInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setToPositionInput(value); // Allow empty string

                      if (value === "") {
                        return; // Don't filter while empty
                      }

                      const to = parseInt(value);
                      if (isNaN(to) || to < 1) return;

                      const from = fromPosition || 1;
                      if (from <= to && to <= allAvailableTeams.length) {
                        handlePositionChange(from, to);
                      }
                    }}
                    onBlur={(e) => {
                      // If empty on blur, set to max
                      if (e.target.value === "") {
                        const from = fromPosition || 1;
                        handlePositionChange(from, allAvailableTeams.length);
                        setToPositionInput(String(allAvailableTeams.length));
                      }
                    }}
                  />
                )}
              </Field>
            </FormGrid>
            <p className="complete-bracket__count">
              Selected: {rankedTeams.length} team(s)
              {fromPosition && toPosition && ` (positions ${fromPosition}-${toPosition})`}
            </p>
          </div>

          <div className="complete-bracket__teams">
            <p className="complete-bracket__label">Selected Teams:</p>
            {rankedTeams.length > 0 && (
              <ol className="complete-bracket__list">
                {rankedTeams.map((team) => (
                  <li key={team.teamId} className="complete-bracket__team">
                    <span className="complete-bracket__position">{team.position}.</span>
                    <span className="complete-bracket__team-name">{team.teamName}</span>
                    {team.groupName && <span className="complete-bracket__group">({team.groupName})</span>}
                  </li>
                ))}
              </ol>
            )}
            {rankedTeams.length === 0 && (
              <EmptyState compact title="No teams in this position range" className="complete-bracket__empty" />
            )}
          </div>

          <p className="complete-bracket__matches">Matches to be populated: {previewMatches.length}</p>
        </div>
      )}
    </Dialog>
  );
};

export default CompleteBracketDialog;
